import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { TextField } from '../components/TextField.tsx';
import { DEFAULT_LOCALE, PASSWORD_MIN_LENGTH } from '../config.ts';
import { useAuth } from '../features/auth/useAuth.ts';
import {
  hasErrors,
  validateRegister,
  type FieldErrors,
  type RegisterField,
} from '../lib/validation.ts';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const consentId = useId();

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<RegisterField>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitError(null);

    const errors = validateRegister({ email, username, password, consent });
    setFieldErrors(errors);
    if (hasErrors(errors)) return;

    setPending(true);
    try {
      await register({
        email: email.trim(),
        username: username.trim(),
        password,
        locale: DEFAULT_LOCALE,
      });
      // El registro deja la sesión abierta: se entra directamente.
      void navigate('/', { replace: true });
    } catch (error) {
      // Email o nombre de usuario ya en uso llega como `account_already_exists` (409)
      // y ErrorAlert lo traduce por su código.
      setSubmitError(error);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <h2 className="mb-4 text-lg font-bold">{t('auth.register.title')}</h2>

      <ErrorAlert error={submitError} />

      <form onSubmit={handleSubmit} noValidate data-test="formulario-registro">
        <TextField
          label={t('auth.fields.email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
          error={fieldErrors.email ? t(fieldErrors.email) : undefined}
        />
        <TextField
          label={t('auth.fields.username')}
          type="text"
          name="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          hint={t('auth.fields.usernameHint')}
          error={fieldErrors.username ? t(fieldErrors.username) : undefined}
        />
        <TextField
          label={t('auth.fields.password')}
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          hint={t('auth.fields.passwordHint', { min: PASSWORD_MIN_LENGTH })}
          error={
            fieldErrors.password
              ? t(fieldErrors.password, { min: PASSWORD_MIN_LENGTH, max: 128 })
              : undefined
          }
        />

        {/* RGPD: la casilla NO viene marcada. Es un requisito, no un detalle de estilo. */}
        <div className="mb-4">
          <div className="flex items-start gap-2.5">
            <input
              id={consentId}
              type="checkbox"
              name="consent"
              className="mt-1 size-5 shrink-0"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              aria-invalid={fieldErrors.consent ? 'true' : undefined}
            />
            <label htmlFor={consentId} className="text-sm">
              {t('auth.fields.consent')}
              <span className="mt-0.5 block text-xs text-tenue">
                {t('auth.fields.consentDetail')}
              </span>
            </label>
          </div>
          {fieldErrors.consent ? (
            <span className="campo-error mt-1 block text-sm text-alerta">
              {t(fieldErrors.consent)}
            </span>
          ) : null}
        </div>

        <button className="btn-primario" type="submit" disabled={pending}>
          {pending ? t('auth.register.pending') : t('auth.register.submit')}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-tenue">
        {t('auth.register.haveAccount')} <Link to="/login" className="text-acento">{t('auth.register.goToLogin')}</Link>
      </p>
    </>
  );
}
